import {useEffect} from 'react';
import {useAppDispatch, useAppSelector} from '.';
import {CompanyDTO} from '../models/company/CompanyDTO';
import {CompanySelector} from '../store/company/CompanySelector';
import {EventSelector} from '../store/events/EventSelector';
import {fetchFutureEvents} from '../store/events/EventThunks';
import {HubblSelector} from '../store/hubbl/HubblSelector';
import {
  fetchPendingBreaks,
  fetchPendingHubbles,
  fetchScheduledBreaks,
  fetchScheduledHubbles,
} from '../store/hubbl/HubblThunks';
import {UserSelector} from '../store/user/UserSelector';
import {useAuth} from '../store/AuthProvider';

/**
 * Hook that handles fetching all social activity data
 * Encapsulates the logic from SceneEmployeeHomeInternal for reuse
 */
export const useSocialActivityDataLoader = () => {
  const dispatch = useAppDispatch();
  const {user: authUser, isLoading: isAuthLoading} = useAuth();

  // User and company data
  const user = useAppSelector(UserSelector.getUserDetails);
  const company = useAppSelector(
    CompanySelector.getCompanyDetails,
  ) as CompanyDTO | null;

  // Events state
  const {loading: eventsLoading, hasLoadedFutureEvents} = useAppSelector(
    EventSelector.getEvents,
  );

  // Hubbles loading states
  const pendingHubblesLoading = useAppSelector(
    HubblSelector.getPendingHubblesLoading,
  );
  const scheduledHubblesLoading = useAppSelector(
    HubblSelector.getScheduledHubblesLoading,
  );

  // Has loaded states to prevent refetching
  const pendingHubblesHasLoaded = useAppSelector(
    HubblSelector.getPendingHubblesHasLoaded,
  );
  const scheduledHubblesHasLoaded = useAppSelector(
    HubblSelector.getScheduledHubblesHasLoaded,
  );

  // Fetch all data effect
  useEffect(() => {
    const loadData = async () => {
      if (isAuthLoading || !authUser || !user || !company) return;

      try {
        const promises = [];

        // Fetch events if not loaded and not currently loading
        if (!hasLoadedFutureEvents && !eventsLoading) {
          promises.push(dispatch(fetchFutureEvents({isAdmin: false})));
        }

        // Fetch hubbles and breaks together since they share loading state
        if (!pendingHubblesHasLoaded && !pendingHubblesLoading) {
          promises.push(dispatch(fetchPendingHubbles({userId: user.id})));
          promises.push(dispatch(fetchPendingBreaks({userId: user.id})));
        }

        if (!scheduledHubblesHasLoaded && !scheduledHubblesLoading) {
          promises.push(dispatch(fetchScheduledHubbles({userId: user.id})));
          promises.push(dispatch(fetchScheduledBreaks({userId: user.id})));
        }

        // Only dispatch if there are promises to execute
        if (promises.length > 0) {
          await Promise.all(promises);
        }
      } catch (error) {
        console.error('Error in useSocialActivityDataLoader:', error);
      }
    };

    loadData();
  }, [
    authUser,
    company,
    dispatch,
    eventsLoading,
    hasLoadedFutureEvents,
    isAuthLoading,
    pendingHubblesHasLoaded,
    pendingHubblesLoading,
    scheduledHubblesHasLoaded,
    scheduledHubblesLoading,
    user,
  ]);

  // Return loading state and data availability info
  return {
    isLoading:
      eventsLoading || pendingHubblesLoading || scheduledHubblesLoading,
    hasLoadedSomeData:
      hasLoadedFutureEvents ||
      pendingHubblesHasLoaded ||
      scheduledHubblesHasLoaded,
    user,
    company,
  };
};
